/*
 * Главная страница (@backstage/plugin-home): сверху поиск, затем развёрнутые ресурсы —
 * кластеры и модели со статусами, ниже быстрые действия, избранное и недавние.
 */
import { Grid, makeStyles } from '@material-ui/core';
import CategoryIcon from '@material-ui/icons/Category';
import CodeIcon from '@material-ui/icons/Code';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import { Content, Header, LinkButton, Page } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  HomePageRecentlyVisited,
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { routes } from '../../lib/viva';
import {
  BrokerIcon,
  DatabaseIcon,
  KubernetesIcon,
  MlModelIcon,
  ServiceIcon,
} from '../icons';
import { DeployedClustersCard } from './DeployedClustersCard';
import { DeployedModelsCard } from './DeployedModelsCard';
import { ServicesCard } from './ServicesCard';
import { DatabasesCard } from './DatabasesCard';
import { BrokersCard } from './BrokersCard';
import { SummaryTiles } from './SummaryTiles';
import { GitlabUpdateNotice } from './GitlabUpdateNotice';
import { useResources } from './useResources';

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50px',
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  // Кнопки в шапке: цвет текста шапки, а не палитры — шапка залита градиентом темы
  ghost: {
    color: theme.page.fontColor,
    borderColor: 'rgba(255,255,255,.35)',
    background: 'rgba(255,255,255,.12)',
    '&:hover': {
      background: 'rgba(255,255,255,.2)',
      borderColor: 'rgba(255,255,255,.5)',
    },
  },
  solid: {
    background:
      theme.palette.type === 'dark' ? theme.palette.primary.main : '#fff',
    color:
      theme.palette.type === 'dark'
        ? theme.palette.primary.contrastText
        : theme.palette.primary.main,
    '&:hover': {
      background:
        theme.palette.type === 'dark' ? theme.palette.primary.light : '#f0f0ff',
    },
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const integrations = useApi(scmIntegrationsApiRef);
  const title = config.getOptionalString('app.title') ?? 'ViVA';
  const gitlabUrl = integrations.gitlab.list()[0]?.config.baseUrl;
  const resources = useResources();

  const tools = [
    { label: 'Новая ML-модель', url: routes.newModel, icon: <MlModelIcon /> },
    { label: 'Новый сервис', url: routes.newService, icon: <ServiceIcon /> },
    {
      label: 'Репозиторий признаков (Feast)',
      url: routes.newFeatureRepo,
      icon: <MlModelIcon />,
    },
    {
      label: 'Новый кластер',
      url: routes.newCluster,
      icon: <KubernetesIcon />,
    },
    {
      label: 'Создать кластер Kafka',
      url: routes.newKafka,
      icon: <BrokerIcon />,
    },
    {
      label: 'Создать кластер PostgreSQL',
      url: routes.newPostgres,
      icon: <DatabaseIcon />,
    },
    { label: 'Каталог', url: routes.catalog, icon: <CategoryIcon /> },
    { label: 'TechDocs', url: routes.docs, icon: <LibraryBooks /> },
    ...(gitlabUrl
      ? [{ label: 'GitLab', url: gitlabUrl, icon: <CodeIcon /> }]
      : []),
  ];

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Header
          title={title}
          subtitle="ML-платформа: кластеры, модели и золотой путь до сервинга"
          pageTitleOverride="Главная"
        >
          <div className={classes.headerActions}>
            <LinkButton
              variant="outlined"
              className={classes.ghost}
              to={routes.newCluster}
              startIcon={<KubernetesIcon />}
            >
              Новый кластер
            </LinkButton>
            <LinkButton
              variant="contained"
              className={classes.solid}
              to={routes.newModel}
              startIcon={<MlModelIcon />}
            >
              Новая модель
            </LinkButton>
          </div>
        </Header>
        <Content>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Поиск по каталогу и документации"
              />
            </Grid>
            <GitlabUpdateNotice />
            <Grid item xs={12}>
              <SummaryTiles resources={resources} />
            </Grid>
            <Grid item xs={12}>
              <DeployedClustersCard resources={resources} />
            </Grid>
            <Grid item xs={12}>
              <DeployedModelsCard resources={resources} />
            </Grid>
            <Grid item xs={12}>
              <ServicesCard resources={resources} />
            </Grid>
            <Grid item xs={12}>
              <DatabasesCard resources={resources} />
            </Grid>
            <Grid item xs={12}>
              <BrokersCard resources={resources} />
            </Grid>
            <Grid item xs={12} md={4}>
              <HomePageToolkit title="Быстрые действия" tools={tools} />
            </Grid>
            <Grid item xs={12} md={4}>
              <HomePageStarredEntities title="Избранное" />
            </Grid>
            <Grid item xs={12} md={4}>
              <HomePageRecentlyVisited title="Недавно открытые" />
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
